const SECTION_ID = 'stl-main-modal-component';
const DESKTOP_MIN = 993;
const RESIZE_DEBOUNCE_MS = 150;
const SECTION_CACHE_MAX = 6;
const IMAGE_CACHE_MAX = 48;

// window.addToCart runs three unaborted round trips (/cart.js, /cart/add.js,
// /cart.js again), so a hung one never settles. Bound the checkout wait instead.
const ADD_TO_CART_TIMEOUT_MS = 15000;
const ERROR_BANNER_MS = 3000;
const ADD_TIMED_OUT = Symbol('stl-add-timed-out');

const VARIANT_PREFETCH_SEL =
  '[data-stl-product-url], .product-color_swatch__wrap[data-product-url]';

const JSON_SCRIPT_SEL = [
  'script[data-stl-base-variant-options]',
  'script[data-stl-variants]'
];

const imageCache = new Map();

const stripWidth = src => String(src ?? '').replace(/([?&])width=\d+/g, '');

const getSrcsetUrls = srcset =>
  String(srcset ?? '')
    .split(',')
    .map(item => item.trim().split(/\s+/)[0])
    .filter(Boolean);

const buildMainImageUrls = src => {
  const cleanSrc = stripWidth(src);
  if (!cleanSrc) return null;

  const separator = cleanSrc.includes('?') ? '&' : '?';
  const w500 = `${cleanSrc}${separator}width=500`;
  const w1000 = `${cleanSrc}${separator}width=1000`;

  return {
    src: w1000,
    srcset: `${w500} 1x, ${w1000} 2x`,
    preload: [w500, w1000]
  };
};

const preloadImage = url => {
  const cleanUrl = String(url ?? '').trim();

  if (!cleanUrl) return Promise.resolve();

  if (imageCache.has(cleanUrl)) {
    return imageCache.get(cleanUrl);
  }

  const promise = new Promise(resolve => {
    const img = new Image();

    img.onload = () =>
      img.decode ? img.decode().then(resolve).catch(resolve) : resolve();

    img.onerror = resolve;
    img.src = cleanUrl;
  });

  imageCache.set(cleanUrl, promise);

  if (imageCache.size > IMAGE_CACHE_MAX) {
    imageCache.delete(imageCache.keys().next().value);
  }

  return promise;
};

const preloadUrls = urls =>
  Promise.all([...new Set(urls.filter(Boolean))].map(preloadImage));

const preloadMediaRoot = root => {
  if (!root) return;

  const urls = new Set();

  root.querySelectorAll('img[src]').forEach(img => {
    const isMainImage = img.closest('.image-container__wrap');
    const isPreviewImage = img.closest('.image-container__first');

    if (isPreviewImage) {
      urls.add(img.src);

      getSrcsetUrls(img.srcset).forEach(url => urls.add(url));

      img
        .closest('picture')
        ?.querySelector('source[srcset]')
        ?.srcset?.split(',')
        .map(item => item.trim().split(/\s+/)[0])
        .filter(Boolean)
        .forEach(url => urls.add(url));
    }

    if (isMainImage) {
      buildMainImageUrls(img.src)?.preload.forEach(url => urls.add(url));
    }
  });

  void preloadUrls([...urls]);
};

/**
 * Shop the Look modal host — `<stl-modal-section id="stl-section-modal-root">`.
 */
class StlModalSection extends HTMLElement {
  #abort = null;
  #fetching = false;
  #resizeTimer = null;
  #sectionCache = new Map();
  // `#pendingAdd` is non-null only while an add is in flight; it is dropped when
  // that add settles, so it never stands for "an add completed at some point".
  #pendingAdd = null;
  #pendingAddVariant = null;
  #addSeq = 0;
  #checkoutPending = false;

  get modal() {
    return this.querySelector('.shop-look__modal');
  }

  get isDesktop() {
    return window.innerWidth >= DESKTOP_MIN;
  }

  get isB2b() {
    return document.body.classList.contains('tw-b2b');
  }

  connectedCallback() {
    if (this.#abort) return;

    if (
      !window.StlModalAnalytics ||
      !window.StlModalVariant ||
      !window.StlModalSwatches
    ) {
      console.error(
        "[stl-modal-section] Missing STL scripts. Use {% render 'stl-modal-scripts' %} before this file."
      );
      return;
    }

    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this._analytics = window.StlModalAnalytics;
    this._variant = window.StlModalVariant;
    this._swatches = window.StlModalSwatches;

    window.__stlModalSectionDeps = {
      getInjectedModal: () => this.modal,
      isB2bBody: () => this.isB2b,
      fetchAndApplyStlModal: (url, opts) =>
        this.fetchAndApplyStlModal(url, opts)
    };

    this.#bindHostEvents(signal);

    document.addEventListener('click', e => this.#onDocumentOpen(e), {
      signal
    });
    document.addEventListener('keydown', e => this.#onDocumentOpen(e), {
      signal
    });
    document.addEventListener('click', e => this.#onDocumentModal(e), {
      signal
    });
    document.addEventListener('keydown', e => this.#onDocumentModalKey(e), {
      signal
    });
    document.addEventListener('keydown', e => this.#onEscape(e), { signal });
    window.addEventListener('resize', () => this.#onResize(), { signal });
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#abort = null;
    if (document.getElementById('stl-section-modal-root') === this) {
      delete window.__stlModalSectionDeps;
    }
  }

  #sectionFetchUrl(productUrl) {
    const sep = productUrl.includes('?') ? '&' : '?';
    return `${productUrl}${sep}section_id=${encodeURIComponent(SECTION_ID)}`;
  }

  #trimSectionCache() {
    if (this.#sectionCache.size <= SECTION_CACHE_MAX) return;
    const sorted = [...this.#sectionCache.entries()].sort(
      (a, b) => (a[1].ts ?? 0) - (b[1].ts ?? 0)
    );
    sorted
      .slice(0, sorted.length - SECTION_CACHE_MAX)
      .forEach(([key]) => this.#sectionCache.delete(key));
  }

  #prefetchSection(productUrl) {
    const fetchUrl = this.#sectionFetchUrl(productUrl);
    const cached = this.#sectionCache.get(fetchUrl);
    if (cached?.html || cached?.promise) return;

    const entry = { ts: Date.now() };
    entry.promise = fetch(fetchUrl, {
      credentials: 'same-origin',
      headers: { Accept: 'text/html' }
    })
      .then(res => {
        if (!res.ok) throw new Error(res.status);
        return res.text();
      })
      .then(html => {
        entry.html = html;
        entry.ts = Date.now();
        preloadMediaRoot(new DOMParser().parseFromString(html, 'text/html'));
        return html;
      })
      .catch(() => {
        this.#sectionCache.delete(fetchUrl);
      });

    this.#sectionCache.set(fetchUrl, entry);
    this.#trimSectionCache();
  }

  async #fetchSectionHtml(productUrl) {
    const fetchUrl = this.#sectionFetchUrl(productUrl);
    const cached = this.#sectionCache.get(fetchUrl);

    if (cached?.html) return cached.html;
    if (cached?.promise) {
      const html = await cached.promise;
      if (html) return html;
    }

    const res = await fetch(fetchUrl, {
      credentials: 'same-origin',
      headers: { Accept: 'text/html' }
    });
    if (!res.ok) throw new Error(`STL section ${res.status}`);
    const html = await res.text();

    this.#sectionCache.set(fetchUrl, {
      html,
      ts: Date.now(),
      promise: Promise.resolve(html)
    });
    this.#trimSectionCache();
    preloadMediaRoot(new DOMParser().parseFromString(html, 'text/html'));
    return html;
  }

  #extractModal(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const section = doc.querySelector('[id^="shopify-section-"]');
    return (
      section?.querySelector('shop-look-modal.shop-look__modal') ??
      section?.querySelector('.shop-look__modal') ??
      doc.querySelector('.shop-look__modal')
    );
  }

  #bindHostEvents(signal) {
    this.addEventListener(
      'pointerenter',
      e => {
        if (!this.modal?.classList.contains('active')) return;

        const thumb = e.target.closest('.image-container__wrap:not(.choosen)');
        if (thumb?.querySelector('img[src]')) {
          const built = buildMainImageUrls(thumb.querySelector('img').src);
          if (built) void preloadUrls(built.preload);
          return;
        }

        const pick = e.target.closest(VARIANT_PREFETCH_SEL);
        if (
          pick &&
          this.modal.contains(pick) &&
          !pick.classList.contains('active') &&
          !pick.classList.contains('swatch-active')
        ) {
          const url = pick.dataset.stlProductUrl || pick.dataset.productUrl;
          if (url) this.#prefetchSection(url);
        }
      },
      { signal, passive: true }
    );

    this.addEventListener(
      'focusin',
      e => {
        const pick = e.target.closest(VARIANT_PREFETCH_SEL);
        if (!pick || !this.modal?.contains(pick)) return;
        const url = pick.dataset.stlProductUrl || pick.dataset.productUrl;
        if (url) this.#prefetchSection(url);
      },
      { signal }
    );

    this.addEventListener(
      'click',
      e => {
        const modal = this.modal;
        if (!modal?.classList.contains('active')) return;

        const title = e.target.closest('.dropdown_title.shop-look-modal');
        if (title && this.contains(title)) {
          const content = title.nextElementSibling;
          const container = title.closest('.dropdown_wr');
          if (!content?.classList.contains('dropdown_content') || !container) {
            return;
          }

          const open =
            content.style.maxHeight !== '0px' && content.style.maxHeight !== '';
          const svg = title.querySelector('svg');

          if (open) {
            content.style.maxHeight = '0';
            container.style.paddingBottom = '0';
            container.classList.remove('active');
            if (svg) svg.style.transform = 'rotate(0deg)';
            window.dropDownSuppEvent?.(
              'close',
              title.textContent.trim(),
              'product_pop_up'
            );
          } else {
            content.style.maxHeight = `${content.scrollHeight}px`;
            container.style.paddingBottom = this.isDesktop ? '20px' : '24px';
            container.classList.add('active');
            if (svg) svg.style.transform = 'rotate(180deg)';
            window.dropDownSuppEvent?.(
              'open',
              title.textContent.trim(),
              'product_pop_up'
            );
          }
          return;
        }

        const openBtn = e.target.closest('[data-open-ingred-modal]');
        if (openBtn && this.contains(openBtn)) {
          const ingred = openBtn.nextElementSibling?.matches?.(
            'shop-look-modal-ingred-image'
          )
            ? openBtn.nextElementSibling
            : openBtn
                .closest('.shop-look__modal, shop-look-modal')
                ?.querySelector('shop-look-modal-ingred-image');
          this.#setIngredientsOpen(ingred, true);
          return;
        }

        const closeBtn = e.target.closest(
          'shop-look-modal-ingred-image .close-button'
        );
        if (closeBtn && this.contains(closeBtn)) {
          this.#setIngredientsOpen(
            closeBtn.closest('shop-look-modal-ingred-image'),
            false
          );
          return;
        }

        const ingredOverlay = e.target.closest('shop-look-modal-ingred-image');
        if (
          ingredOverlay &&
          e.target === ingredOverlay &&
          this.contains(ingredOverlay)
        ) {
          this.#setIngredientsOpen(ingredOverlay, false);
          return;
        }

        const sizeLabel = e.target.closest(
          '.shop-look__modal-sizes .stl-size-option-label, .shop-look__modal-sizes label[for]'
        );
        if (sizeLabel && modal.contains(sizeLabel)) {
          const input =
            document.getElementById(sizeLabel.getAttribute('for') ?? '') ??
            sizeLabel.closest('.item')?.querySelector('input[type="radio"]');
          if (input?.name?.startsWith('stl-size-')) {
            requestAnimationFrame(() =>
              this._variant.applyStlSizeSelectionFromInput(input)
            );
          }
          return;
        }

        const sizeRadio = e.target.closest(
          '.shop-look__modal-sizes input[type="radio"][name^="stl-size-"]'
        );
        if (sizeRadio && modal.contains(sizeRadio)) {
          requestAnimationFrame(() =>
            this._variant.applyStlSizeSelectionFromInput(sizeRadio)
          );
          return;
        }

        const wrap = e.target.closest('.image-container__wrap');
        if (!wrap || !modal.contains(wrap)) return;

        wrap
          .closest('.image-container')
          ?.querySelectorAll('.image-container__wrap')
          .forEach(w => w.classList.remove('choosen'));
        wrap.classList.add('choosen');
        void this.#syncMainImage(modal);
      },
      { signal }
    );

    this.addEventListener(
      'change',
      e => {
        const input = e.target;
        if (
          !(input instanceof HTMLInputElement) ||
          input.type !== 'radio' ||
          !input.name?.startsWith('stl-size-') ||
          !this.modal?.contains(input)
        ) {
          return;
        }

        const modal = this.modal;
        const prev = modal.dataset.stlLastSizeSelection ?? '';
        const next = input.value;
        if (prev && prev !== next) {
          this._analytics.fireProductSizeChangeAnalytics(modal, prev, next);
        }
        this._variant.applyStlSizeSelectionFromInput(input);
        modal.dataset.stlLastSizeSelection = next;
      },
      { signal }
    );
  }

  #setIngredientsOpen(ingredModal, isOpen) {
    if (!ingredModal || (ingredModal.style.display === 'flex') === isOpen) {
      return;
    }
    ingredModal.style.display = isOpen ? 'flex' : 'none';
    this._analytics.handleStlIngredientsAnalytics(isOpen);
  }

  async #syncMainImage(modal) {
    const thumb = modal.querySelector('.image-container__wrap.choosen img');
    const first = modal.querySelector('.image-container__first');
    if (!thumb || !first) return;

    const urls = buildMainImageUrls(thumb.src);
    if (!urls) return;

    await preloadUrls(urls.preload);

    let target = first.querySelector('img');
    if (!target) {
      target = document.createElement('img');
      (first.querySelector('picture') ?? first).appendChild(target);
    }

    target.src = urls.src;
    target.srcset = urls.srcset;
    target.alt = thumb.alt || '';
    target.removeAttribute('sizes');

    const source = target.closest('picture')?.querySelector('source');
    if (source) source.srcset = urls.srcset;
  }

  #layoutModal(modal) {
    const content = modal.querySelector('.shop-look__modal-content');
    const form = modal.querySelector('.shop-the-look__form');
    const btnDesktop = modal.querySelector('.btn-details.desktop');

    if (this.isDesktop && content && form && btnDesktop) {
      content.append(form, btnDesktop);
    }

    if (!this.isDesktop) {
      const media = modal.querySelector('.shop-look__modal-media');
      const contentWrap = modal.querySelector('.content-wrap');
      if (
        media &&
        contentWrap &&
        !media.parentElement?.classList.contains('new-wrapper')
      ) {
        const wrap = document.createElement('div');
        wrap.classList.add('new-wrapper');
        media.parentNode.insertBefore(wrap, media);
        wrap.append(media, contentWrap);
      }
    }
  }

  #resetDropdowns() {
    this.querySelectorAll('.dropdown_content').forEach(el => {
      el.style.maxHeight = '0';
    });
    this.querySelectorAll('.dropdown_wr').forEach(el => {
      el.classList.remove('active');
      el.style.paddingBottom = '0';
    });
    this.querySelectorAll('.dropdown_title.shop-look-modal svg').forEach(
      svg => {
        svg.style.transform = 'rotate(0deg)';
      }
    );
  }

  #syncHostAttrs(existing, imported) {
    [...existing.attributes].forEach(attr => {
      if (attr.name !== 'class' && !imported.hasAttribute(attr.name)) {
        existing.removeAttribute(attr.name);
      }
    });
    [...imported.attributes].forEach(attr => {
      if (attr.name !== 'class') existing.setAttribute(attr.name, attr.value);
    });
  }

  #swapInPlace(existing, imported) {
    this.#syncHostAttrs(existing, imported);

    const existingWrap = existing.querySelector('.shop-look__modal-wrap');
    const importedWrap = imported.querySelector('.shop-look__modal-wrap');
    if (!existingWrap || !importedWrap) return false;

    existingWrap.replaceChildren(...importedWrap.childNodes);

    JSON_SCRIPT_SEL.forEach(sel => {
      const oldScript = existing.querySelector(sel);
      const newScript = imported.querySelector(sel);
      if (oldScript && newScript)
        oldScript.replaceWith(newScript.cloneNode(true));
      else if (newScript) existing.append(newScript.cloneNode(true));
      else oldScript?.remove();
    });
    return true;
  }

  #mountImported(imported, replaceWhileOpen) {
    const existing = this.modal;
    const swapped =
      replaceWhileOpen &&
      existing?.classList.contains('active') &&
      this.#swapInPlace(existing, imported);

    if (!swapped) {
      const prev = this.firstElementChild;
      if (prev) this.replaceChildren(imported);
      else this.append(imported);
    }

    const modal = this.modal;
    this.#layoutModal(modal);
    this.#resetDropdowns();
    void this.#syncMainImage(modal);
    return modal;
  }

  #bindFormSubmit() {
    const form = this.querySelector('.shop-the-look__form');
    if (!form || form.dataset.stlFormBound === 'true') return;
    form.dataset.stlFormBound = 'true';

    form.addEventListener('submit', ev => {
      ev.preventDefault();

      const modal = this.modal;
      const checked = modal?.querySelector(
        '.shop-look__modal-sizes input[type="radio"]:checked'
      );
      if (checked) this._variant.applyStlSizeSelectionFromInput(checked);

      const addBtn =
        (ev.submitter?.classList?.contains('shop-the-look__button') &&
          ev.submitter) ||
        form.querySelector(
          '#addProduct, .shop-the-look__button:not(.bottom-btn)'
        ) ||
        form.querySelector('.shop-the-look__button');

      const variantNum = Number(
        form.querySelector('.product-variant-id')?.value ?? ''
      );
      if (!Number.isFinite(variantNum) || variantNum <= 0) return;

      if (addBtn?.classList.contains('notify')) {
        const path = this.querySelector('.btn-details')?.href;
        document.dispatchEvent(
          new CustomEvent('notify_available_popup', {
            detail: {
              productId: form.id,
              variantId: String(variantNum),
              productHandle: path ? new URL(path, location.origin).pathname : ''
            }
          })
        );
        return;
      }

      if (typeof window.addToCart !== 'function') return;

      const spinner = addBtn?.querySelector('.loading-overlay__spinner');
      addBtn?.classList.add('loading');
      spinner?.classList.remove('hidden');

      const addId = ++this.#addSeq;
      this.#pendingAddVariant = variantNum;
      this.#pendingAdd = (async () => {
        let cartAfterAdd = null;
        try {
          const status = await window.addToCart(
            [{ id: variantNum, quantity: 1 }],
            'shop-the-look'
          );
          this._analytics.fireStlAddToCartCustomAnalytics(modal, variantNum);

          if (status && typeof window.fetchCart === 'function') {
            const keys = [
              window.__slideCartFragment || 'slide-cart-fragment',
              'slide-cart-fragment'
            ];
            const sectionHtml = keys
              .map(k => status.sections?.[k])
              .find(Boolean);
            cartAfterAdd = await window.fetchCart(sectionHtml);
            const bubble = document.querySelector('.cart-count-bubble span');
            if (bubble && cartAfterAdd?.item_count != null) {
              bubble.textContent = String(cartAfterAdd.item_count);
              document
                .querySelector('.cart-count-bubble')
                ?.classList.remove('hidden');
            }
            window.pushKlaviyoEvent?.(cartAfterAdd);
          }

          if (document.body.dataset.template?.startsWith('cart')) {
            await window.cartPageRefreshSections?.();
          }

          document.dispatchEvent(
            new CustomEvent('cart:added', {
              detail: { source: 'shop-the-look' }
            })
          );
          document.body.classList.add('body_added_to_cart');
          this.#close('add_to_cart');
          // window.addToCart swallows some failures and returns a truthy `{}`
          // (e.g. "Special offers found for this product"), so only a response
          // carrying the added cart lines proves the cart is non-empty.
          const ok = Array.isArray(status?.items) && status.items.length > 0;
          // Post-add lines, so start_checkout_click can be pushed synchronously
          // before the redirect. Prefer the refetched cart over the added lines.
          const refetched = cartAfterAdd?.items;
          const items =
            Array.isArray(refetched) && refetched.length > 0
              ? refetched
              : ok
                ? status.items
                : null;
          // The swallowed-failure branch returns before addToCart reaches its own
          // error banner, so the caller has to show one. A falsy status already
          // showed one there, so flagging it here would give the customer two.
          return { ok, items, silent: Boolean(status) && !ok };
        } catch {
          /* addToCart shows error UI */
          return { ok: false, items: null, silent: false };
        } finally {
          addBtn?.classList.remove('loading');
          spinner?.classList.add('hidden');
          document.body.classList.remove('body_added_to_cart');
          // Only the newest add owns these fields, so a late settle from an
          // older add cannot drop a newer one that is still in flight.
          if (this.#addSeq === addId) this.#dropPendingAdd();
        }
      })();
    });
  }

  #teardownSticky(modal) {
    modal?.__stlStickyObserver?.disconnect();
    modal.__stlStickyObserver = null;
  }

  #setupSticky(modal) {
    this.#teardownSticky(modal);

    const bottom = modal.querySelector('.shop-the-look__button.bottom-btn');
    if (!this.isDesktop) {
      bottom?.classList.remove('sticky-label');
      bottom?.setAttribute('aria-hidden', 'true');
      return;
    }

    const primary = modal.querySelector(
      '.shop-the-look__form .product-form__buttons .shop-the-look__button'
    );
    if (!primary || !bottom) return;

    if (typeof IntersectionObserver !== 'function') {
      bottom.classList.add('sticky-label');
      bottom.removeAttribute('aria-hidden');
      return;
    }

    let scrollRoot = primary.parentElement;
    while (scrollRoot && scrollRoot !== document.body) {
      const { overflowY } = getComputedStyle(scrollRoot);
      if (/auto|scroll|overlay/.test(overflowY)) break;
      scrollRoot = scrollRoot.parentElement;
    }
    if (scrollRoot === document.body) scrollRoot = null;

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        const stick =
          !entry.isIntersecting &&
          entry.boundingClientRect.bottom <= (entry.rootBounds?.top ?? 0);
        bottom.classList.toggle('sticky-label', stick);
        bottom.toggleAttribute('aria-hidden', !stick);
      },
      { root: scrollRoot, threshold: 0 }
    );
    obs.observe(primary);
    modal.__stlStickyObserver = obs;
  }

  #finalize(modal, scrollState) {
    this.#bindFormSubmit();
    modal.classList.add('active');
    this.classList.add('active');
    document.body.classList.add('shop-the-look');

    this._variant.syncStlFormFromCheckedSize(modal);
    this._variant.syncStlLastSizeDataset(modal);
    this._swatches.hashes.syncActive(modal);
    preloadMediaRoot(modal);
    modal.querySelectorAll(VARIANT_PREFETCH_SEL).forEach(el => {
      const url = el.dataset.stlProductUrl || el.dataset.productUrl;
      if (url) this.#prefetchSection(url);
    });

    if (scrollState) {
      requestAnimationFrame(() => {
        modal.querySelector('.shop-look__modal-content')?.scrollTo({
          top: scrollState.contentTop
        });
        modal.querySelector('.image-container')?.scrollTo({
          left: scrollState.imageLeft
        });
      });
    }

    this.#setupSticky(modal);
    return modal;
  }

  async fetchAndApplyStlModal(productUrl, { replaceWhileOpen = false } = {}) {
    if (!productUrl || this.#fetching) return null;

    const modalBefore = replaceWhileOpen ? this.modal : null;
    let scrollState = null;

    if (modalBefore) {
      this.#setIngredientsOpen(
        modalBefore.querySelector('shop-look-modal-ingred-image'),
        false
      );
      scrollState = {
        contentTop:
          modalBefore.querySelector('.shop-look__modal-content')?.scrollTop ??
          0,
        imageLeft:
          modalBefore.querySelector('.image-container')?.scrollLeft ?? 0
      };
      modalBefore.dataset.stlSwapLoading = 'true';
      modalBefore.style.pointerEvents = 'none';
      this.classList.add('stl-swap-dim');
    }

    this.#fetching = true;

    try {
      const html = await this.#fetchSectionHtml(productUrl);
      const modalNode = this.#extractModal(html);
      if (!modalNode) throw new Error('STL modal markup missing');

      const imported = document.importNode(modalNode, true);
      const hashes = this._swatches.hashes.capture(imported);

      if (!replaceWhileOpen) {
        const modal = this.#mountImported(imported, false);
        this.#finalize(modal);
        this._swatches.clones.snapshotFromModal(modal);
        return modal;
      }

      preloadMediaRoot(imported);
      this._swatches.clones.restoreForSwap(imported);
      this._swatches.hashes.apply(imported, hashes);

      const modal = this.#mountImported(imported, true);
      return this.#finalize(modal, scrollState);
    } catch (err) {
      console.error('[stl-modal-section]', err);
      return null;
    } finally {
      this.#fetching = false;
      this.classList.remove('stl-swap-dim');
      if (modalBefore?.isConnected) {
        delete modalBefore.dataset.stlSwapLoading;
        modalBefore.style.pointerEvents = '';
      }
    }
  }

  async #openFromItem(item) {
    const productUrl = item.dataset.stlProductUrl;
    if (!productUrl || this.#fetching) return;

    const list = item.closest('.shop-look__list');
    const items = list ? [...list.querySelectorAll('.shop-look__item')] : [];
    const index = items.indexOf(item) + 1 || Number(item.dataset.index) || 1;
    this._analytics.firePopupView(item, index);

    item.setAttribute('data-stl-loading', 'true');
    try {
      await this.fetchAndApplyStlModal(productUrl, { replaceWhileOpen: false });
    } finally {
      item.removeAttribute('data-stl-loading');
    }
  }

  #close(closeType = 'close_button') {
    const modal = this.modal;
    if (!modal?.classList.contains('active')) return;

    this.#setIngredientsOpen(
      modal.querySelector('shop-look-modal-ingred-image'),
      false
    );
    this.#teardownSticky(modal);

    modal.classList.remove('active');
    this.classList.remove('active');
    document.body.classList.remove('shop-the-look');

    window.clearSupplements?.();
    window.shopTheLookOPenCloseEvent?.(
      'product_popup_close_click',
      JSON.stringify({
        from: this._analytics.getStlAnalyticsSource(),
        type: closeType
      })
    );
  }

  #resolveOpenTarget(e) {
    const target = e.target;
    const stlHost = target.closest('[data-stl]');
    const shopItem = stlHost && target.closest('.shop-look__item');
    if (shopItem && stlHost.contains(shopItem)) return shopItem;

    if (
      target.closest('.shop-look__item[data-stl], [data-stl] .shop-look__item')
    ) {
      return null;
    }

    const launch = target.closest('[data-stl-product-url]');
    if (!launch || launch.closest('.shop-look__modal')) return null;
    if (e.type === 'keydown' && launch.tagName !== 'A') return null;

    if (launch.tagName === 'A' && e.type === 'click') e.preventDefault();
    if (this.#fetching) return launch;
    return launch;
  }

  #onDocumentOpen(e) {
    if (this.isB2b) return;
    if (e.type === 'keydown' && e.key !== 'Enter' && e.key !== ' ') return;

    const item = this.#resolveOpenTarget(e);
    if (!item) return;

    if (e.type === 'keydown') e.preventDefault();
    if (this.#fetching && e.type === 'click') return;
    void this.#openFromItem(item);
  }

  #onDocumentModal(e) {
    if (!this.contains(e.target)) return;

    const modal = this.modal;
    if (this._swatches.picks.bundleVariant(e, modal)) return;
    if (this._swatches.picks.colorSwatch(e, this, modal)) return;

    const checkout = e.target.closest('.addProduct');
    if (
      checkout &&
      modal?.contains(checkout) &&
      !checkout.classList.contains('hidden')
    ) {
      e.preventDefault();
      e.stopPropagation();
      const primary = modal.querySelector(
        '.shop-the-look__form .product-form__buttons .shop-the-look__button'
      );
      if (
        primary &&
        !primary.disabled &&
        !primary.classList.contains('notify')
      ) {
        // An unhandled rejection here would leave the guard latched and the CTA
        // dead, so reset the same state the `finally` owns.
        this.#addThenCheckout(primary, checkout).catch(() => {
          this.#checkoutPending = false;
          checkout.classList.remove('is-loading');
        });
      }
      return;
    }

    if (e.target.closest('.shop-look__close-btn')) {
      this.#close('close_button');
      return;
    }

    const backdrop = modal?.querySelector('.shop-look__modal-item-wrapper');
    if ((backdrop && e.target === backdrop) || (modal && e.target === modal)) {
      this.#close('area');
    }
  }

  // Navigation must wait for /cart/add.js to resolve: leaving for /checkout
  // while the add is still in flight hits checkout with an empty cart and
  // Shopify bounces the customer to the homepage.
  async #addThenCheckout(primary, trigger) {
    if (this.#checkoutPending) return;
    this.#checkoutPending = true;
    // The spinner lands on the Add to Cart button, so without this the element
    // the customer actually clicked gives no feedback for the whole wait. The
    // class only kills pointer events, so the element stays tabbable and needs
    // the aria pair to say the same thing to a screen reader.
    trigger?.classList.add('is-loading');
    trigger?.setAttribute('aria-busy', 'true');
    trigger?.setAttribute('aria-disabled', 'true');

    try {
      // element.click() ignores `pointer-events: none`, so forcing a submit
      // while the same variant is already being added lands it twice. Reuse the
      // in-flight add instead, but only when it is for the current selection.
      // Dropping first means a submit that bails out leaves nothing to await,
      // so we can never navigate on some other variant's add.
      if (!this.#isAddingVariant(this.#selectedVariantId(primary))) {
        this.#dropPendingAdd();
        primary.click();
      }

      const added = await this.#awaitAdd(this.#pendingAdd);
      if (added === ADD_TIMED_OUT) {
        this.#dropPendingAdd();
        this.#clearAddLoading();
        this.#showError(window.cartStrings?.error);
        return;
      }
      if (!added?.ok) {
        // Without this the pending state just clears and the CTA looks dead, and
        // every retry repeats it, because nothing else surfaces this branch.
        if (added?.silent) this.#showError(window.cartStrings?.error);
        return;
      }

      // Passing items keeps pushStartCheckoutClick off its /cart.js branch: that
      // fetch is aborted by the redirect below, so the event never lands.
      window.pushStartCheckoutClick?.(
        added.items,
        '.addProduct',
        'shop_the_look',
        'store_checkout'
      );
      window.location.href = `${window.Shopify?.routes?.root ?? '/'}checkout`;
    } finally {
      this.#checkoutPending = false;
      trigger?.classList.remove('is-loading');
      trigger?.removeAttribute('aria-busy');
      trigger?.removeAttribute('aria-disabled');
    }
  }

  // `.product-variant-id` is rewritten by applyStlSizeSelectionFromInput on
  // every size and colour pick, so it always holds the current selection.
  #selectedVariantId(primary) {
    const form = primary?.closest('.shop-the-look__form');
    const id = Number(form?.querySelector('.product-variant-id')?.value ?? '');
    return Number.isFinite(id) && id > 0 ? id : null;
  }

  #isAddingVariant(variantId) {
    return (
      !!this.#pendingAdd &&
      variantId != null &&
      this.#pendingAddVariant === variantId
    );
  }

  #dropPendingAdd() {
    this.#pendingAdd = null;
    this.#pendingAddVariant = null;
  }

  // Never navigate on timeout: /checkout with an empty cart is the bounce this
  // ticket fixes. Resolve a sentinel so the caller can recover in place.
  #awaitAdd(pending) {
    if (!pending) return Promise.resolve(false);

    return new Promise(resolve => {
      const timer = setTimeout(
        () => resolve(ADD_TIMED_OUT),
        ADD_TO_CART_TIMEOUT_MS
      );
      const settle = value => {
        clearTimeout(timer);
        resolve(value);
      };
      pending.then(settle, () => settle(false));
    });
  }

  // The add's own `finally` cannot clear its spinner while it is still hung. The
  // spinner sits on whichever CTA was submitted, so clear every one of them.
  #clearAddLoading() {
    this.modal?.querySelectorAll('.shop-the-look__button').forEach(btn => {
      btn.classList.remove('loading');
      btn.querySelector('.loading-overlay__spinner')?.classList.add('hidden');
    });
    document.body.classList.remove('body_added_to_cart');
  }

  #showError(message) {
    if (!message) return;

    const wrapper = document.querySelector('.error_message_wrapper');
    const text = wrapper?.querySelector('.error_message');
    if (!wrapper || !text) return;

    text.textContent = message;
    wrapper.classList.remove('hidden');
    setTimeout(() => {
      wrapper.classList.add('hidden');
    }, ERROR_BANNER_MS);
  }

  #onDocumentModalKey(e) {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    if (this.isB2b || !this.contains(e.target)) return;

    const addLink = e.target.closest('.addProduct:not(.hidden)');
    if (addLink) {
      e.preventDefault();
      addLink.click();
      return;
    }

    const pick = e.target.closest(
      '.stl-bundle-variant-pick[data-stl-product-url], .stl-supplement-flavor-pick[data-stl-product-url]'
    );
    if (
      pick &&
      !pick.classList.contains('active') &&
      pick.closest('.stl-bundle-palette-root, .stl-supplement-flavor-palette')
    ) {
      e.preventDefault();
      pick.click();
    }
  }

  #onEscape(e) {
    if (e.key === 'Escape' && this.modal?.classList.contains('active')) {
      this.#close('keyboard');
    }
  }

  #onResize() {
    const modal = this.modal;
    if (!modal?.classList.contains('active')) return;
    clearTimeout(this.#resizeTimer);
    this.#resizeTimer = setTimeout(() => {
      this.#layoutModal(modal);
      this.#setupSticky(modal);
    }, RESIZE_DEBOUNCE_MS);
  }
}

if (!customElements.get('stl-modal-section')) {
  customElements.define('stl-modal-section', StlModalSection);
}
